import { useState } from "react";
import { Check, Landmark, Pencil, Plus, Trash2, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { accountKindLabels, bankLabels, formatDate, formatSEK0 } from "../../lib/format";
import { ASSET_KINDS, LIABILITY_KINDS, isLiabilityKind } from "../../types";
import type { AccountKind, DerivedBankAccount, ManualAccount } from "../../types";

interface AccountsSectionProps {
  accounts: ManualAccount[] | null;
  derived: DerivedBankAccount[] | null;
  onRefresh: () => void;
}

const EMPTY_FORM = {
  name: "",
  kind: "bank" as AccountKind,
  balance: "",
  owner: "",
  institution: "",
  creditLimit: "",
  interestRate: "",
};

export function AccountsSection({ accounts, derived, onRefresh }: AccountsSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBalance, setEditBalance] = useState("");

  async function handleAdd() {
    const balance = Number(form.balance.replace(/\s/g, "").replace(",", "."));
    if (!form.name.trim() || !Number.isFinite(balance) || balance < 0) return;

    await fetch("/api/accounts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: form.name.trim(),
        kind: form.kind,
        balance,
        owner: form.owner.trim() || undefined,
        institution: form.institution.trim() || undefined,
        creditLimit: form.creditLimit
          ? Number(form.creditLimit.replace(/\s/g, "").replace(",", "."))
          : undefined,
        interestRate: form.interestRate
          ? Number(form.interestRate.replace(",", "."))
          : undefined,
      }),
    });
    setForm(EMPTY_FORM);
    setShowAddForm(false);
    onRefresh();
  }

  async function handleSaveBalance(id: string) {
    const balance = Number(editBalance.replace(/\s/g, "").replace(",", "."));
    if (Number.isFinite(balance) && balance >= 0) {
      await fetch(`/api/accounts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ balance }),
      });
      onRefresh();
    }
    setEditingId(null);
  }

  async function handleDelete(id: string) {
    await fetch(`/api/accounts/${id}`, { method: "DELETE" });
    onRefresh();
  }

  async function handleToggleDerived(sourceId: string, included: boolean) {
    await fetch("/api/accounts/derived", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceId, included }),
    });
    onRefresh();
  }

  if (!accounts || !derived) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const assetAccounts = accounts.filter((a) => !isLiabilityKind(a.kind));
  const liabilityAccounts = accounts.filter((a) => isLiabilityKind(a.kind));
  const creditCards = liabilityAccounts.filter((a) => a.kind === "credit_card");
  const otherLiabilities = liabilityAccounts.filter((a) => a.kind !== "credit_card");

  const isCreditCardForm = form.kind === "credit_card";
  const isLoanForm = form.kind === "loan" || form.kind === "mortgage";

  function renderAccountRow(account: ManualAccount) {
    const utilization =
      account.kind === "credit_card" && account.creditLimit && account.creditLimit > 0
        ? Math.min(Math.round((account.balance / account.creditLimit) * 100), 999)
        : null;

    return (
      <div key={account.id} className="group rounded-lg border p-3">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="truncate font-medium">{account.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {accountKindLabels[account.kind]}
                {account.owner ? ` · ${account.owner}` : ""}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {[account.institution, account.interestRate ? `${account.interestRate}% ränta` : null]
                .filter(Boolean)
                .join(" · ") || `Uppdaterad ${formatDate(account.updatedAt.slice(0, 10))}`}
            </div>
          </div>

          {editingId === account.id ? (
            <div className="flex items-center gap-1">
              <Input
                className="h-8 w-28 text-right text-sm"
                inputMode="decimal"
                value={editBalance}
                onChange={(e) => setEditBalance(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleSaveBalance(account.id);
                  if (e.key === "Escape") setEditingId(null);
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-success"
                onClick={() => void handleSaveBalance(account.id)}
                aria-label="Spara saldo"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setEditingId(null)}
                aria-label="Avbryt"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <span
                className={`font-semibold tabular-nums ${
                  isLiabilityKind(account.kind) ? "text-destructive" : ""
                }`}
              >
                {formatSEK0(account.balance)}
              </span>
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 max-lg:opacity-60"
                  onClick={() => {
                    setEditingId(account.id);
                    setEditBalance(String(account.balance));
                  }}
                  aria-label={`Uppdatera saldo för ${account.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 max-lg:opacity-60"
                  onClick={() => void handleDelete(account.id)}
                  aria-label={`Ta bort ${account.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </>
          )}
        </div>

        {utilization !== null && (
          <div className="mt-2">
            <div className="mb-1 flex justify-between text-xs text-muted-foreground">
              <span>Utnyttjande</span>
              <span className="tabular-nums">
                {utilization}% av {formatSEK0(account.creditLimit!)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${
                  utilization > 80
                    ? "bg-destructive"
                    : utilization > 50
                      ? "bg-warning"
                      : "bg-primary"
                }`}
                style={{ width: `${Math.min(utilization, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Derived bank balances */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" />
            Bankkonton från kontoutdrag
          </CardTitle>
          <CardDescription>
            Senast kända saldo per konto, hämtat automatiskt ur dina uppladdade
            kontoutdrag. Bocka ur konton som inte ska räknas med i
            nettoförmögenheten (t.ex. dubbletter).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {derived.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Inga saldon hittade ännu — ladda upp kontoutdrag som innehåller en
              saldokolumn.
            </p>
          ) : (
            <div className="space-y-2">
              {derived.map((account) => (
                <label
                  key={account.sourceId}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border p-3"
                >
                  <input
                    type="checkbox"
                    checked={account.includedInNetWorth}
                    onChange={(e) =>
                      void handleToggleDerived(account.sourceId, e.target.checked)
                    }
                    className="h-4 w-4 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {bankLabels[account.bank] ?? account.bank}
                      {account.label !== account.bank && (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          · {account.label.replace(`${account.bank} · `, "")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      per {formatDate(account.balanceDate)}
                    </div>
                  </div>
                  <span
                    className={`font-semibold tabular-nums ${account.includedInNetWorth ? "" : "text-muted-foreground line-through"}`}
                  >
                    {formatSEK0(account.balance)}
                  </span>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add account */}
      <Card>
        <CardHeader>
          <CardTitle>Konton, kort & lån</CardTitle>
          <CardDescription>
            Lägg till det som inte syns i kontoutdragen: sparkonton, bostad, bil,
            kreditkort, bolån. Saldot uppdaterar du själv — historiken sparas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showAddForm ? (
            <Button variant="outline" onClick={() => setShowAddForm(true)}>
              <Plus className="mr-2 h-4 w-4" /> Lägg till konto
            </Button>
          ) : (
            <div className="space-y-2 rounded-lg border p-3">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <Input
                  placeholder="Namn (t.ex. Amex Guld)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                />
                <Select
                  value={form.kind}
                  onValueChange={(v) => setForm({ ...form, kind: v as AccountKind })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {accountKindLabels[kind]}
                      </SelectItem>
                    ))}
                    {LIABILITY_KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {accountKindLabels[kind]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={
                    isLiabilityKind(form.kind) ? "Skuld/saldo (kr)" : "Saldo/värde (kr)"
                  }
                  inputMode="decimal"
                  value={form.balance}
                  onChange={(e) => setForm({ ...form, balance: e.target.value })}
                />
                <Input
                  placeholder="Ägare (valfritt)"
                  value={form.owner}
                  onChange={(e) => setForm({ ...form, owner: e.target.value })}
                />
                <Input
                  placeholder="Bank/institut (valfritt)"
                  value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                />
                {isCreditCardForm && (
                  <Input
                    placeholder="Kreditgräns (kr)"
                    inputMode="decimal"
                    value={form.creditLimit}
                    onChange={(e) => setForm({ ...form, creditLimit: e.target.value })}
                  />
                )}
                {isLoanForm && (
                  <Input
                    placeholder="Ränta (%)"
                    inputMode="decimal"
                    value={form.interestRate}
                    onChange={(e) => setForm({ ...form, interestRate: e.target.value })}
                  />
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => void handleAdd()} disabled={!form.name.trim() || !form.balance}>
                  Spara
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddForm(false);
                    setForm(EMPTY_FORM);
                  }}
                >
                  Avbryt
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {creditCards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Kreditkort</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">{creditCards.map(renderAccountRow)}</CardContent>
        </Card>
      )}

      {assetAccounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tillgångar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">{assetAccounts.map(renderAccountRow)}</CardContent>
        </Card>
      )}

      {otherLiabilities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Lån & skulder</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">{otherLiabilities.map(renderAccountRow)}</CardContent>
        </Card>
      )}
    </div>
  );
}
